import * as d3 from 'd3';
import * as _ from 'lodash';

import { CategoricalChartData } from '../CategoricalChart/CategoricalChart.model';
import { XAxis } from './xAxis';
import { YAxis } from './yAxis';
import { Bars } from '../CategoricalChart/bars';
import { ElementRender } from '../CategoricalChart/ElementeRenderer';
import { IHeight, IWidth, ItemFormat, IRender } from '../interfaces';
import { Labels } from '../CategoricalChart/labels';
import { VerticalLines } from './verticalLines';

export class HorizontalCategoricalChartRenderer {
    private _width: number;
    private _height: number;
    private _xAxis: XAxis;
    private _yAxis: YAxis;
    private _widths: IWidth[] = [];
    private _heights: IHeight[] = [];
    private _options: { [object: string]: boolean; } = {};
    private _renderers: { [object: string]: (ElementRender & IRender) } = {};
    _itemFormats: ItemFormat[];
    _verticalLines: any;

    constructor(selection: d3.Selection<any>, width: number, height: number, clipPathId: string) {
        selection.classed('categorical-chart', true);
        this._xAxis = new XAxis(selection, width, height);
        this._widths.push(this._xAxis);
        this._heights.push(this._xAxis);
        this._yAxis = new YAxis(selection, width, height);
        this._widths.push(this._yAxis);
        this._heights.push(this._yAxis);

        this._verticalLines = new VerticalLines(selection, 'vertical-gridlines')
            .classes('gridline');
        this._widths.push(this._verticalLines);
        this._heights.push(this._verticalLines);
        const seriesContainer = selection.append('g')
            .classed('series', true)
            .style({
                'clip-path': `url(#${clipPathId})`
            });
        const bars = new Bars(seriesContainer)
            .visible(true)
            .addListener('mouseenter', (d, selection) => {
                // const crosshairWidth = this._tooltip.width();
                // const crosshairHeight = this._tooltip.height();
                // let x = d.x + d.width;
                // if (x + crosshairWidth > this._width) {
                //     x = d.x - crosshairWidth;
                // }
                // let y = d.y - (d.item.value < 0 ? d.height : 0);
                // if (y + crosshairHeight > this._height) {
                //     y -= crosshairHeight;
                // } else if (y < 0) {
                //     y = 0;
                // }
                // this._tooltip.visible(true)
                //     .position({ x, y })
                //     .render(d);
            })
            .addListener('mouseout', (d, selection) => {
                // this._tooltip.visible(false)
                //     .render(d);
            });
        this._renderers['bars'] = bars;
        // this._renderers['line'] = line;
        // this._renderers['minmax'] = new MinMax(seriesContainer);
        this._renderers['labels'] = new Labels(seriesContainer);
        // this._widths.push(this._horizontalLines);
        // this._heights.push(this._horizontalLines);
        // this._tooltip = new Tooltip(seriesContainer);
        // this._crosshairLine = new CrosshairLine(seriesContainer)
        //     .addListener('mouseenter', (d, selection) => {
        //         this._tooltip.visible(true);
        //     })
        //     .addListener('mousemove', (d, selection) => {
        //         const pos = this._xAxis.index(d[0]);
        //         if (pos !== undefined && this._itemFormats) {
        //             const itemFormat = this._itemFormats[pos];
        //             let x = itemFormat.x + itemFormat.width / 2;
        //             const crosshairWidth = this._tooltip.width();
        //             if (x + crosshairWidth > this._width) {
        //                 x -= crosshairWidth;
        //             }
        //             this._tooltip//.visible(true)
        //                 .position({ x, y: 0 })
        //                 .render(itemFormat);
        //             this._crosshairLine.render(itemFormat);
        //         }
        //     })
        //     .addListener('mouseout', (d, selection) => {
        //         this._tooltip.visible(false)
        //             .render(null);
        //     });
        // this._widths.push(this._crosshairLine);
        // this._heights.push(this._crosshairLine);
        this.width(width)
            .height(height);
    }

    // color(): string;
    // color(value: string): this;
    // color(value?: string): this | string {
    //     let line = this._renderers['line'] as Line;
    //     if (!arguments.length) {
    //         return line.color();
    //     }
    //     line.color(value);
    //     return this;
    // }

    height(): number;
    height(value: number): this;
    height(value?: number): this | number {
        if (!arguments.length) {
            return this._height;
        }
        this._heights.forEach(r => r.height(value));
        for (const key of Object.keys(this._renderers)) {
            this._renderers[key].height(value);
        }
        this._height = value;
        return this;
    }

    width(): number;
    width(value: number): this;
    width(value?: number): this | number {
        if (!arguments.length) {
            return this._width;
        }
        this._widths.forEach(r => r.width(value));
        for (const key of Object.keys(this._renderers)) {
            this._renderers[key].width(value);
        }
        this._width = value;
        return this;
    }

    xDomain(): [number, number];
    xDomain(value: number[]): this;
    xDomain(value?: number[]): this | number[] {
        if (!arguments.length) {
            return this._xAxis.extent();
        }
        this._xAxis.extent(value);
        return this;
    }
    xTickFormat(): string;
    xTickFormat(value: string): this;
    xTickFormat(value?: string): string | this {
        if (!arguments.length) {
            return null;
        }
        this._xAxis.tickFormat(value);
        return this;
    }

    yWidth(): number;
    yWidth(value: number): this;
    yWidth(value?: number): this | number {
        if (!arguments.length) {
            return this._yAxis.width();
        }
        this._yAxis.width(value);
        return this;
    }

    spacing(): number;
    spacing(value: number): this;
    spacing(value?: number): this | number {
        if (!arguments.length) {
            return this._yAxis.spacing();
        }
        this._yAxis.spacing(value);
        return this;
    }

    options(): { [object: string]: boolean };
    options(value: { [object: string]: boolean }): this;
    options(value?: { [object: string]: boolean }): this | { [object: string]: boolean } {
        if (!arguments.length) {
            return this._options;
        }
        this._options = value;
        return this;
    }

    render(data: CategoricalChartData[]): void {
        this._xAxis
            .render(data);
        this._yAxis
            .render(data);
        const barFormat: ItemFormat[] = this.toItemFormat(data);
        this._itemFormats = barFormat;
        for (const key of Object.keys(this._renderers)) {
            const isVisible = this._options && this._options[key];
            this._renderers[key]
                .visible(isVisible)
                .render(barFormat);
        }
        this._verticalLines
            .visible(this._options['verticalLines'])
            .render(this._xAxis.ticks().map(d => {
                return { x: this._xAxis.position(d) };
            }));
        // this._crosshairLine
        //     .visible(!this._options['bars'] && !this._options['circles'])
        //     .render(null);
    }

    private toItemFormat(data: CategoricalChartData[]) {
        const zero = this._xAxis.position(0);
        return data.map(d => {
            const y = this._yAxis.position(d);
            const height = this._yAxis.band();
            const x = this._xAxis.position(d.value);
            const width = Math.abs(x - zero);
            const res: ItemFormat = {
                item: d,
                x: zero,
                y,
                width,
                height,
                color: d.color,
                y1: 0,
                y2: 0,
                translate: { x: d.value < 0 ? -width : 0, y: 0 },
                tooltip: d.tooltip,
                onClick: function () {
                    if (d.clickCallback) {
                        d.clickCallback();
                    }
                }
            };
            return res;
        });
    }
}
